import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faGripDotsVertical } from '@fortawesome/sharp-solid-svg-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TableColumn } from './types';

import './column_manager_modal.css';

interface ColumnManagerItem {
  FieldName: string;
  Label: string;
  Visible: boolean;
}

interface SortableColumnItemProps {
  item: ColumnManagerItem;
  onToggleVisibility: (fieldName: string) => void;
}

function SortableColumnItem({ item, onToggleVisibility }: SortableColumnItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.FieldName,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sc-column-manager-item ${isDragging ? 'sc-column-manager-item-dragging' : ''}`}
    >
      <span className="sc-column-manager-item-drag-handle" {...attributes} {...listeners}>
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </span>
      <span className="sc-column-manager-item-label">{item.Label}</span>
      <button
        type="button"
        className="sc-column-manager-item-visibility"
        onClick={() => onToggleVisibility(item.FieldName)}
        title={item.Visible ? 'Hide column' : 'Show column'}
      >
        <FontAwesomeIcon icon={item.Visible ? faEye : faEyeSlash} />
      </button>
    </div>
  );
}

interface ColumnManagerModalProps {
  columns: TableColumn[];
  columnOrder: string[];
  selectedColumns: string[];
  onApply: (order: string[], visibleColumns: string[]) => void;
  onReset: () => void;
  onClose: () => void;
}

function ColumnManagerModal({
  columns,
  columnOrder,
  selectedColumns,
  onApply,
  onReset,
  onClose,
}: ColumnManagerModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const buildItems = useCallback((): ColumnManagerItem[] => {
    // Start with columns in persisted order, then append any new ones
    const orderedFields =
      columnOrder.length > 0 ? [...columnOrder] : columns.map((c) => c.FieldName);
    const allFieldNames = new Set(columns.map((c) => c.FieldName));
    // Add any columns not in the persisted order at the end
    for (const col of columns) {
      if (!orderedFields.includes(col.FieldName)) {
        orderedFields.push(col.FieldName);
      }
    }
    // Build items, skipping columns that no longer exist
    const hasSelection = selectedColumns.length > 0;
    return orderedFields
      .filter((fn) => allFieldNames.has(fn))
      .map((fn) => {
        const col = columns.find((c) => c.FieldName === fn)!;
        const visible = hasSelection ? selectedColumns.includes(fn) : col.Visible !== false;
        return { FieldName: fn, Label: col.Label, Visible: visible };
      });
  }, [columns, columnOrder, selectedColumns]);

  const [items, setItems] = useState<ColumnManagerItem[]>(buildItems);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.FieldName === active.id);
      const newIndex = prev.findIndex((i) => i.FieldName === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const toggleVisibility = useCallback((fieldName: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.FieldName === fieldName ? { ...item, Visible: !item.Visible } : item,
      ),
    );
  }, []);

  const handleApply = useCallback(() => {
    const order = items.map((i) => i.FieldName);
    const visible = items.filter((i) => i.Visible).map((i) => i.FieldName);
    onApply(order, visible);
    onClose();
  }, [items, onApply, onClose]);

  const handleReset = useCallback(() => {
    onReset();
    onClose();
  }, [onReset, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="sc-column-manager-modal" ref={panelRef}>
      <div className="sc-column-manager-header">Manage Columns</div>
      <div className="sc-column-manager-list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={items.map((i) => i.FieldName)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableColumnItem
                key={item.FieldName}
                item={item}
                onToggleVisibility={toggleVisibility}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div className="sc-column-manager-actions">
        <button
          type="button"
          className="eone-ui-button-small eone-ui-button-secondary"
          onClick={handleApply}
        >
          Apply
        </button>
        <button
          type="button"
          className="eone-ui-button-small eone-ui-button-tertiary"
          onClick={handleReset}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

export { ColumnManagerModal };
